/**
 * 프리셋 단독 import/export — 결정 #6(포맷 교환) + #23(공유 메타). 프리셋은
 * 프로바이더 비종속이라(결정 #7) 그대로 공유 가능하다. 표준 교환 포맷이 제각각
 * 이므로 우리 래퍼(`roqeet-preset_v1`)로 직렬화한다.
 *
 * 플랫폼 중립(결정 #10): 문자열 ↔ 도메인 엔티티 변환만. import는 createPreset로
 * 새 id/sync 메타를 부여한다(붙여넣기 시 충돌 방지).
 */
import type { Preset } from "../domain/index.js";
import { createPreset } from "../factory/entities.js";

type AnyObj = Record<string, unknown>;

/** 프리셋을 단독 JSON 문자열로 직렬화한다(id/sync 제외 — 받는 쪽이 새로 부여). */
export function exportPresetToJson(preset: Preset): string {
  // 휘발성 메타(id/sync)는 빼고 공유 가능한 정의만 담는다(결정 #20/#23).
  const { id: _id, sync: _sync, ...shareable } = preset;
  void _id;
  void _sync;
  return JSON.stringify({ spec: "roqeet-preset_v1", data: shareable }, null, 2);
}

/**
 * JSON 텍스트에서 프리셋을 임포트한다. 우리 래퍼(spec=roqeet-preset_v1)와, data
 * 없이 평면으로 담긴 프리셋 객체 둘 다 지원한다(결정 #6).
 */
export function importPresetFromJson(text: string): Preset {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    throw new Error("프리셋 JSON을 해석할 수 없습니다.");
  }
  const root = parsed as AnyObj;
  const data: AnyObj =
    root["spec"] === "roqeet-preset_v1" && root["data"] && typeof root["data"] === "object"
      ? (root["data"] as AnyObj)
      : root;

  // 프리셋의 최소 식별: blocks 배열 또는 sampling 객체가 있어야 함.
  const hasBlocks = Array.isArray(data["blocks"]);
  const hasSampling = !!data["sampling"] && typeof data["sampling"] === "object";
  if (!hasBlocks && !hasSampling) {
    throw new Error("유효한 프리셋 데이터를 찾을 수 없습니다(blocks/sampling 없음).");
  }

  const name = typeof data["name"] === "string" ? (data["name"] as string) : "가져온 프리셋";
  const init: Partial<Preset> & { name: string } = { name };
  if (data["apiStyle"] === "chat" || data["apiStyle"] === "text") {
    init.apiStyle = data["apiStyle"];
  }
  if (hasSampling) init.sampling = data["sampling"] as Preset["sampling"];
  if (hasBlocks) init.blocks = data["blocks"] as Preset["blocks"];
  if (data["instructTemplate"] && typeof data["instructTemplate"] === "object") {
    init.instructTemplate = data["instructTemplate"] as Preset["instructTemplate"];
  }
  if (data["share"] && typeof data["share"] === "object") {
    init.share = data["share"] as Preset["share"];
  }
  if (data["extensions"] && typeof data["extensions"] === "object") {
    init.extensions = data["extensions"] as Preset["extensions"];
  }

  return createPreset(init);
}
